// 표준 정규분포 누적확률 근사 (Abramowitz-Stegun)
// 최대 오차 약 7.5×10⁻⁸ 수준으로 정밀하여, 점수 분포를 정규분포에 따라 정확히 산출한다.
export function normalCdf(z: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp((-z * z) / 2);
  let p = d * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  if (z > 0) p = 1 - p;
  return p;
}

// 상위 % 표시 포맷: 큰 값은 정수, 작은 값은 소수로 정밀 표기
export function formatTopPercentile(p: number): string {
  if (p >= 10) return `${Math.round(p)}`;
  if (p >= 1) return p.toFixed(1);
  return p >= 0.1 ? p.toFixed(1) : p.toFixed(2);
}

// 9등급 경계 (누적 상위 %)
// 1등급: 상위 0~4% / 2등급: 4~11% / 3등급: 11~23% / 4등급: 23~40% / 5등급: 40~60%
// 6등급: 60~77% / 7등급: 77~89% / 8등급: 89~96% / 9등급: 96~100%
export const GRADE_BOUNDARIES: { grade: number; max: number }[] = [
  { grade: 1, max: 4 },
  { grade: 2, max: 11 },
  { grade: 3, max: 23 },
  { grade: 4, max: 40 },
  { grade: 5, max: 60 },
  { grade: 6, max: 77 },
  { grade: 7, max: 89 },
  { grade: 8, max: 96 },
  { grade: 9, max: 100 },
];

// 상위 백분위(%) → 등급(1~9)
export function gradeFromTopPercentile(topPct: number): number {
  for (const b of GRADE_BOUNDARIES) {
    if (topPct <= b.max) return b.grade;
  }
  return 9;
}

export interface GradeInfo {
  grade: number;
  label: string;
  range: string;
  description: string;
}

const GRADE_INFO_MAP: Record<number, GradeInfo> = {
  1: { grade: 1, label: '1등급', range: '상위 0~4%', description: '최상위 수준입니다.' },
  2: { grade: 2, label: '2등급', range: '상위 4~11%', description: '최상위권 수준입니다.' },
  3: { grade: 3, label: '3등급', range: '상위 11~23%', description: '상위권 수준입니다.' },
  4: { grade: 4, label: '4등급', range: '상위 23~40%', description: '중상위권 수준입니다.' },
  5: { grade: 5, label: '5등급', range: '상위 40~60%', description: '중위권 수준입니다.' },
  6: { grade: 6, label: '6등급', range: '상위 60~77%', description: '중하위권 수준입니다.' },
  7: { grade: 7, label: '7등급', range: '상위 77~89%', description: '하위권 수준입니다.' },
  8: { grade: 8, label: '8등급', range: '상위 89~96%', description: '최하위권 수준입니다.' },
  9: { grade: 9, label: '9등급', range: '상위 96~100%', description: '최하위 수준입니다.' },
};

// 상위 백분위(%) → 등급 정보(라벨·범위·설명 포함)
export function getGradeInfo(topPct: number): GradeInfo {
  return GRADE_INFO_MAP[gradeFromTopPercentile(topPct)];
}

// 상위 백분위(%) → "상위 X%" 또는 "하위 X%" 표시
// topPct는 "나보다 높은 점수의 비율"이므로 50% 이하면 상위, 초과면 하위로 표현한다.
export function topPercentileLabel(topPct: number): { direction: '상위' | '하위'; value: string } {
  if (topPct <= 50) {
    return { direction: '상위', value: formatTopPercentile(topPct) };
  }
  return { direction: '하위', value: formatTopPercentile(100 - topPct) };
}

// 어휘력·문해력 정답률(%) → 상위 백분위(%)
// 정답률이 평균 60%, 표준편차 15%의 정규분포를 따른다고 가정한다.
// 반환값은 "나보다 높은 점수의 비율"로, 높을수록 낮은 수준을 의미한다.
export function literacyTopPercentile(totalPct: number): number {
  const mean = 60;
  const sd = 15;
  const z = (totalPct - mean) / sd;
  return (1 - normalCdf(z)) * 100;
}

// 영어 단어 검사 정답률(%) → 상위 백분위(%)
// 학년(초등/중등/고등)별로 난이도가 달라 평균·표준편차를 다르게 적용한다.
// 초등 단어는 상대적으로 쉬워 평균이 높고, 고등 단어는 평균이 낮다.
// 반환값은 "나보다 높은 점수의 비율"로, 높을수록 낮은 수준을 의미한다.
export function englishTopPercentile(totalPct: number, level: string): number {
  const params: Record<string, { mean: number; sd: number }> = {
    elementary: { mean: 75, sd: 12 },
    middle: { mean: 65, sd: 15 },
    high: { mean: 58, sd: 16 },
  };
  const { mean, sd } = params[level] || { mean: 65, sd: 15 };
  const z = (totalPct - mean) / sd;
  return (1 - normalCdf(z)) * 100;
}